// Ported from Frontend/discovery/vsee-clinic/src/components/VSeeClinicWidget.tsx,
// restyled to match this app's Tailwind conventions. Rendered as a fullscreen
// portal (same pattern as intake-myfit-scan's fullscreen 3D viewer) since the
// Jitsi Meet IFrame API's own UI doesn't lay out well in a small embedded box.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { loadVSeeScript } from "@/lib/loadVSeeScript";
import type { VSeeExternalApi, VSeeSessionConfig } from "@/types/vsee";

const EXTERNAL_API_URL =
  "https://clinic.meet.vsee.com/libs/external_api.min.js";

type ConnectionState =
  "loading" | "connecting" | "connected" | "left" | "error";

export default function VSeeClinicWidget({
  session,
  onLeave,
}: {
  session: VSeeSessionConfig;
  onLeave?: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<VSeeExternalApi | null>(null);
  const [state, setState] = useState<ConnectionState>("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    setError(null);

    loadVSeeScript(EXTERNAL_API_URL)
      .then(() => {
        if (cancelled || !containerRef.current || !window.JitsiMeetExternalAPI)
          return;

        setState("connecting");

        const api = new window.JitsiMeetExternalAPI(session.domain, {
          roomName: session.roomName,
          parentNode: containerRef.current,
          userName: session.userName,
          authToken: session.authToken,
          isClinic: session.isClinic ?? true,
          // Jitsi otherwise picks the UI language from the browser/OS locale.
          configOverwrite: { defaultLanguage: "en" },
          width: "100%",
          height: "100%",
        });
        apiRef.current = api;

        api.addListener("videoConferenceJoined", () => {
          if (!cancelled) setState("connected");
        });
        api.addListener("videoConferenceLeft", () => {
          if (!cancelled) setState("left");
        });
        api.addListener("readyToClose", () => {
          if (!cancelled) onLeave?.();
        });
        api.addListener("errorOccurred", (payload) => {
          if (!cancelled) {
            setState("error");
            setError(
              typeof payload === "string" ? payload : "VSee reported an error.",
            );
          }
        });
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setState("error");
          setError(err.message);
        }
      });

    return () => {
      cancelled = true;
      apiRef.current?.dispose();
      apiRef.current = null;
    };
  }, [session, onLeave]);

  const toggleAudio = () => apiRef.current?.executeCommand("toggleAudio");
  const toggleVideo = () => apiRef.current?.executeCommand("toggleVideo");
  const hangUp = () => apiRef.current?.executeCommand("hangup");

  return createPortal(
    <div className="fixed inset-0 z-[999] bg-foreground-950 flex flex-col">
      <div className="flex-shrink-0 flex items-center justify-between gap-3 px-4 py-2.5 bg-foreground-900 text-xs font-semibold text-white/80">
        <span>
          {state === "loading" && "Loading VSee Clinic…"}
          {state === "connecting" && "Connecting to visit…"}
          {state === "connected" && `Connected — room "${session.roomName}"`}
          {state === "left" && "You left the visit."}
          {state === "error" && `Error: ${error}`}
        </span>
        <button
          type="button"
          onClick={() => onLeave?.()}
          aria-label="Close"
          className="w-7 h-7 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors cursor-pointer flex-shrink-0"
        >
          <i className="ri-close-line text-base"></i>
        </button>
      </div>

      <div ref={containerRef} className="flex-1 min-h-0" />

      {state === "connected" && (
        <div className="flex-shrink-0 flex items-center justify-center gap-3 p-3 bg-foreground-900">
          <button
            type="button"
            onClick={toggleAudio}
            className="px-3 py-2 rounded-lg text-xs font-semibold bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap"
          >
            Toggle mic
          </button>
          <button
            type="button"
            onClick={toggleVideo}
            className="px-3 py-2 rounded-lg text-xs font-semibold bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap"
          >
            Toggle camera
          </button>
          <button
            type="button"
            onClick={hangUp}
            className="px-3 py-2 rounded-lg text-xs font-semibold bg-red-500/80 text-white hover:bg-red-500 transition-colors cursor-pointer whitespace-nowrap"
          >
            Leave visit
          </button>
        </div>
      )}

      {session.guestInviteUrl && (
        <p className="flex-shrink-0 px-4 py-2.5 bg-foreground-900 text-[11px] text-white/60 leading-relaxed">
          To show something on a phone&apos;s rear camera while staying
          connected here, open this link on a second device:{" "}
          <a
            href={session.guestInviteUrl}
            target="_blank"
            rel="noreferrer"
            className="text-primary-300 underline"
          >
            {session.guestInviteUrl}
          </a>
        </p>
      )}
    </div>,
    document.body,
  );
}
